import { useCallback, useEffect, useMemo, useState, useRef } from "react";

// Multi-Select ComboBox Component
type MultiSelectComboBoxProps = {
    id: string;
    options: Array<{ value: string; label: string }>;
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    className?: string;
};

const MultiSelectComboBox: React.FC<MultiSelectComboBoxProps> = ({
    id,
    options,
    selected,
    onChange,
    placeholder = "Auswählen...",
    className = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        return options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    const handleToggleOption = (value: string) => {
        const newSelected = selected.includes(value)
            ? selected.filter(v => v !== value)
            : [...selected, value];
        onChange(newSelected);
    };

    const handleRemoveChip = (value: string, event: React.MouseEvent) => {
        event.stopPropagation();
        onChange(selected.filter(v => v !== value));
    };

    const getLabel = (value: string) => options.find(opt => opt.value === value)?.label ?? value;

    return (
        <div ref={containerRef} className={`multi-select-combobox ${className}`} style={{ position: 'relative' }}>
            <div
                className="transport-list-filter-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    padding: '6px',
                    minHeight: '38px',
                    cursor: 'pointer',
                    alignItems: 'center'
                }}
            >
                {selected.length === 0 ? (
                    <span style={{ color: '#999' }}>{placeholder}</span>
                ) : (
                    selected.map(value => (
                        <span
                            key={value}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '12px',
                                fontSize: '13px',
                                color: '#1976d2'
                            }}
                        >
                            {getLabel(value)}
                            <button
                                type="button"
                                onClick={(e) => handleRemoveChip(value, e)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: '#1976d2',
                                    fontSize: '16px',
                                    lineHeight: '1'
                                }}
                                aria-label={`${getLabel(value)} entfernen`}
                            >
                                ×
                            </button>
                        </span>
                    ))
                )}
            </div>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginTop: '2px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                >
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Suchen..."
                        style={{
                            width: '100%',
                            padding: '8px',
                            border: 'none',
                            borderBottom: '1px solid #eee',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <div>
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>
                                Keine Ergebnisse
                            </div>
                        ) : (
                            filteredOptions.map(option => (
                                <div
                                    key={option.value}
                                    onClick={() => handleToggleOption(option.value)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        backgroundColor: selected.includes(option.value) ? '#f5f5f5' : 'transparent'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = selected.includes(option.value) ? '#f5f5f5' : 'transparent';
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(option.value)}
                                        onChange={() => { }}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                    <span>{option.label}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Hilfskomponente für asynchrone Sortimentsnamenanzeige
type SortimentNameDisplayProps = {
    matnr?: string | null;
    fallback?: string | null;
};
const SortimentNameDisplay: React.FC<SortimentNameDisplayProps> = ({ matnr, fallback }) => {
    const [name, setName] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function fetchName() {
            setLoading(true);
            setError(null);
            setName(null);
            try {
                if (!matnr) {
                    setName(fallback ?? "–");
                    setLoading(false);
                    return;
                }
                const maktx = await getSortimentNameForMatnr(matnr);
                if (!cancelled) {
                    setName(maktx ?? fallback ?? "–");
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[SortimentNameDisplay] Error fetching Sortiment name", e);
                if (!cancelled) {
                    setError("Fehler beim Laden");
                    setName(fallback ?? "–");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchName();
        return () => { cancelled = true; };
    }, [matnr, fallback]);

    if (loading) return <span>Lädt…</span>;
    if (error) return <span style={{ color: 'red' }}>{error}</span>;
    return <span>{name ?? "–"}</span>;
};
import { fetchZhbT49GtMap } from "../../../../api/zhbT49Gt";
import { getMatnrForContract, getSortimentNameForMatnr } from "../../../../api/sortiment";
import { fetchLIFNRByContractNumber } from "../../../../api/contracts";
import type { ChangeEvent, FormEvent, ReactElement } from "react";
import QRCode from "qrcode";
import { API_BASE_URL } from "../../../../config";
import { getAuthHeaders } from "../../../../api/auth"; // Import auth headers helper
import { formatResultValue, type FieldFormatDescriptor } from "../../../../standalone/formatting";
import { NotesView } from "../../../../components/NotesView";
import { DriverDataView } from "../../../../components/DriverDataView";
import { TimeWindowEditView } from "../../../../components/TimeWindowEditView";
import { AddBatchTransportView } from "../../../../components/AddBatchTransportView";
import { AssortmentDistributionView } from "../../../../components/AssortmentDistributionView";
import "./TransportListPage.css";

// TenantId wird jetzt aus dem JWT Token extrahiert (Backend holt es automatisch)

// Hilfsfunktion: Berechne aktuelle Kalenderwoche nach ISO 8601
function getCurrentCalendarWeek(): number {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
}

// Hilfsfunktion: Berechne Mindest-Kalenderwoche für Filter
// Aktuelle KW + zukünftige, wenn Montag: auch letzte KW
function getMinimumCalendarWeek(): number {
    const now = new Date();
    const currentWeek = getCurrentCalendarWeek();
    const dayOfWeek = now.getDay(); // 0 = Sonntag, 1 = Montag, ..., 6 = Samstag

    // Wenn Montag (1), zeige auch letzte KW
    if (dayOfWeek === 1) {
        return currentWeek - 1;
    }

    // Sonst nur aktuelle KW + zukünftige
    return currentWeek;
}

interface Transport extends Record<string, unknown> {
    _id: string;
    // SAP Felder
    TRANSPORTNUMMER?: string | null;
    VERTRAG?: string | null;
    VERTRAGSPOSITION?: string | null;
    VERTART?: string | null;
    FRAKTION?: string | null;
    MENGE?: string | null;
    SPEDITEUR?: string | null;
    AB_STRASSE?: boolean | null;
    BEDARF_ID?: string | null;
    GEPLANTE_MENGE?: string | null;
    WERKS?: string | null;
    TPLST?: string | null;
    JAHR?: string | null;
    KALENDERWOCHE?: string | null;
    MATNR?: string | null; // Materialnummer aus Contract LineItems
    // UI-Felder (berechnet)
    FractionBez?: string | null;
    Sortimentbez?: string | null;
    SupplierName?: string | null;
    State?: string | null;
    StartDate?: string | null;
    LoadCount?: string | null;
    carrierName?: string | null;
    plannedQuantity?: string | null; // Summe der quantityTrucks aus batchTransports
    unplannedQuantity?: string | null; // MENGE - plannedQuantity (min 0)
    batchTransportsCount?: string | null; // Anzahl der batchTransports
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface ColumnDefinition {
    key: keyof Transport;
    label: string;
    width?: number;
    format?: FieldFormatDescriptor | null;
    sortable?: boolean;
}

type SortDirection = "asc" | "desc";

interface SortConfig {
    key: ColumnDefinition["key"];
    direction: SortDirection;
}

const NUMBER_FORMAT: FieldFormatDescriptor = { kind: "number" };
const DEFAULT_SORT_PARAM = "TRANSPORTNUMMER:asc";

const STATE_CONFIG: Record<string, { label: string; color: string; title: string }> = {
    ungeplant: { label: "Ungeplant", color: "#dc2626", title: "Ungeplant - Noch keine Planung vorhanden" }, // Rot
    "teilweise verplant": { label: "Teilweise verplant", color: "#f59e0b", title: "Teilweise verplant - Planung läuft" }, // Gelb
    verplant: { label: "Verplant", color: "#16a34a", title: "Verplant - Vollständig eingeplant" }, // Grün
};

const COLUMNS: ColumnDefinition[] = [
    { key: "State", label: "Status", width: 80, sortable: true },
    { key: "TRANSPORTNUMMER", label: "TransportNr.", width: 160, sortable: true },
    { key: "WERKS", label: "Werk", width: 100, sortable: true },
    { key: "SupplierName", label: "Lieferant", width: 110, sortable: true },
    { key: "FRAKTION", label: "Fraktion", width: 180, sortable: true }, // Anzeige wird unten überschrieben
    { key: "carrierName", label: "Spediteur", width: 110, sortable: true },
    { key: "VERTRAG", label: "Vertrag", width: 120, sortable: true },
    { key: "MENGE", label: "LKWs ges.", width: 130, format: NUMBER_FORMAT, sortable: true },
    { key: "plannedQuantity", label: "Geplant", width: 120, format: NUMBER_FORMAT, sortable: false },
    { key: "unplannedQuantity", label: "Ungeplant", width: 100, format: NUMBER_FORMAT, sortable: false },
    { key: "KALENDERWOCHE", label: "KW", width: 60, sortable: true },
];


function parseSortParam(param: string | null): SortConfig | null {
    if (!param) {
        return null;
    }

    const [rawKey, rawDirection] = param.split(":", 2);
    if (!rawKey || !rawDirection) {
        return null;
    }

    const column = COLUMNS.find((candidate) => String(candidate.key) === rawKey);
    if (!column) {
        return null;
    }

    if (rawDirection === "asc" || rawDirection === "desc") {
        return { key: column.key, direction: rawDirection } satisfies SortConfig;
    }

    return null;
}

function buildSortParam(column: ColumnDefinition, direction: SortDirection): string {
    return `${String(column.key)}:${direction}`;
}

interface FetchState {
    status: "idle" | "loading" | "loaded" | "error";
    items: Transport[];
    total: number;
    error?: string;
}

interface FilterFormState {
    TRANSPORTNUMMER: string;
    FRAKTION: string;
    SupplierName: string;
    VERTART: string;
    WERKS: string[];
    State: string;
    KALENDERWOCHE: string;
    Sortimentbez: string;
}

interface AppliedFilters {
    TRANSPORTNUMMER?: string;
    FRAKTION?: string;
    SPEDITEUR?: string;  // Backend erwartet SPEDITEUR (nicht SupplierName)
    VERTART?: string;
    WERKS?: string;
    STATUS?: string;  // Backend erwartet STATUS (nicht State)
    KALENDERWOCHE?: string;
    minKALENDERWOCHE?: string;
    SORTIMENT?: string;  // Backend erwartet SORTIMENT oder MATNR (nicht Sortimentbez)
}

type FilterField = keyof FilterFormState;

const PAGE_TEXT = {
    title: "Zeitfensterbuchung",
    description: "Planung von Sammeltransporten",
    refresh: "Aktualisieren",
    loading: "Lade Daten …",
    emptyTitle: "Keine Transporte",
    emptyDescription: "Derzeit stehen keine Transporte zur Verfügung.",
    error: "Die Daten konnten nicht geladen werden.",
    filters: {
        title: "Filter",
        apply: "Filter anwenden",
        reset: "Zurücksetzen",
        TRANSPORTNUMMER: "Transportnummer",
        FRAKTION: "Fraktion",
        SupplierName: "Spediteur",
        VERTART: "Vertragsart",
        WERKS: "Werk",
        State: "Status",
        KALENDERWOCHE: "Kalenderwoche",
        Sortimentbez: "Sortiment",
    },
};

function buildAppliedFilters(form: FilterFormState): AppliedFilters {
    const next: AppliedFilters = {};

    // IMMER Mindest-KW setzen (aktuelle KW oder letzte KW wenn Montag)
    const minWeek = getMinimumCalendarWeek();
    next.minKALENDERWOCHE = String(minWeek);

    const transportNumber = form.TRANSPORTNUMMER.trim();
    if (transportNumber) {
        next.TRANSPORTNUMMER = transportNumber;
    }

    const fraktion = form.FRAKTION.trim();
    if (fraktion) {
        next.FRAKTION = fraktion;
    }

    const supplierName = form.SupplierName.trim();
    if (supplierName) {
        next.SPEDITEUR = supplierName;  // Backend erwartet SPEDITEUR
    }

    const vertart = form.VERTART.trim();
    if (vertart) {
        next.VERTART = vertart;
    }

    const plants = form.WERKS.filter(w => w.trim());
    if (plants.length > 0) {
        next.WERKS = plants.join(',');
    }

    const state = form.State.trim();
    if (state) {
        next.STATUS = state;  // Backend erwartet STATUS
    }

    const calWeek = form.KALENDERWOCHE.trim();
    if (calWeek) {
        next.KALENDERWOCHE = calWeek;
    }

    const sortimentbez = form.Sortimentbez.trim();
    if (sortimentbez) {
        next.SORTIMENT = sortimentbez;  // Backend erwartet SORTIMENT
    }

    return next;
}

async function extractErrorMessage(response: Response): Promise<string | null> {
    try {
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
            const payload = await response.json();
            if (typeof payload.message === "string" && payload.message) {
                return payload.message;
            }
        }
    } catch {
        // Ignore parse errors
    }
    return null;
}

export function TransportListPage() {
    const [state, setState] = useState<FetchState>({ status: "idle", items: [], total: 0 });
    const [sortParam, setSortParam] = useState<string>(DEFAULT_SORT_PARAM);
    const [isFilterCollapsed, setIsFilterCollapsed] = useState(() => {
        const stored = localStorage.getItem("transportListFilterCollapsed");
        return stored === "true";
    });
    const [selectedTransportId, setSelectedTransportId] = useState<string | null>(null);
    const [batchTransports, setBatchTransports] = useState<Array<Record<string, unknown>>>([]);
    const [loadingBatchTransports, setLoadingBatchTransports] = useState(false);
    const [selectedBatchTransportId, setSelectedBatchTransportId] = useState<string | null>(null);
    const [showNotesView, setShowNotesView] = useState(false);
    const [batchNotes, setBatchNotes] = useState<string[]>([]);
    const [showDriverDataView, setShowDriverDataView] = useState(false);
    const [showTimeWindowEdit, setShowTimeWindowEdit] = useState(false);
    const [showAddBatchTransport, setShowAddBatchTransport] = useState(false);
    const [showAssortmentDistribution, setShowAssortmentDistribution] = useState(false);
    const [batchTransportSaveHandler, setBatchTransportSaveHandler] = useState<(() => Promise<void>) | null>(null);
    const [canSaveBatchTransport, setCanSaveBatchTransport] = useState(false);
    const [batchTransportOverage, setBatchTransportOverage] = useState(0);
    const [timeWindowEditSaveHandler, setTimeWindowEditSaveHandler] = useState<(() => Promise<void>) | null>(null);
    const [canSaveTimeWindowEdit, setCanSaveTimeWindowEdit] = useState(false);
    const [supplierLIFNR, setSupplierLIFNR] = useState<string | null>(null);
    const [loadingSupplierLIFNR, setLoadingSupplierLIFNR] = useState(false);

    const [filterForm, setFilterForm] = useState<FilterFormState>({
        TRANSPORTNUMMER: "",
        FRAKTION: "",
        SupplierName: "",
        VERTART: "",
        WERKS: ["0128", "0123"],
        State: "",
        KALENDERWOCHE: "",
        Sortimentbez: "",
    });

    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(() => {
        // Initial: Nur Transporte ab minimaler KW laden (aktuelle KW, außer Montag: dann auch letzte KW)
        const minKW = getMinimumCalendarWeek();
        // Werke 0128 und 0123 sind initial ausgewählt
        return {
            minKALENDERWOCHE: String(minKW),
            WERKS: "0128,0123"
        };
    });
    const [displayedItemsCount, setDisplayedItemsCount] = useState(20);

    const sortConfig = useMemo(() => parseSortParam(sortParam), [sortParam]);

    const toggleFilterCollapse = () => {
        setIsFilterCollapsed(prev => {
            const newValue = !prev;
            localStorage.setItem("transportListFilterCollapsed", String(newValue));
            return newValue;
        });
    };


    const fetchTransports = useCallback(async () => {
        setState((prev) => ({ ...prev, status: "loading" }));
        try {
            const trimmedSort = sortParam.trim();
            // Build URL with proper base handling for same-origin proxy
            const baseUrl = API_BASE_URL || window.location.origin;
            const url = new URL(`/api/transports`, baseUrl);
            Object.entries(appliedFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    url.searchParams.set(key, String(value));
                }
            });
            if (trimmedSort) {
                url.searchParams.set("sort", trimmedSort);
            }
            const response = await fetch(url.toString(), {
                headers: {
                    ...getAuthHeaders()
                },
                credentials: "include",
            });
            if (response.status === 401) {
                const message = await extractErrorMessage(response);
                throw new Error(message ?? "Authentifizierung erforderlich. Bitte melde dich erneut an.");
            }
            if (!response.ok) {
                const message = await extractErrorMessage(response);
                throw new Error(message ?? `Datenabruf fehlgeschlagen (${response.status})`);
            }
            const items = (await response.json()) as Transport[];
            const total = Array.isArray(items) ? items.length : 0;
            setState({ status: "loaded", items, total });
            setDisplayedItemsCount(20);
        } catch (error) {
            const message = error instanceof Error ? error.message : PAGE_TEXT.error;
            setState((prev) => ({ ...prev, status: "error", error: message }));
        }
    }, [appliedFilters, sortParam]);

    useEffect(() => {
        void fetchTransports();
    }, [fetchTransports]);

    const handleRefresh = useCallback(() => {
        void fetchTransports();
    }, [fetchTransports]);

    const handleRowClick = useCallback(async (transportId: string) => {
        setSelectedTransportId(transportId);
        setLoadingBatchTransports(true);

        // Reset batch transport selection and notes when switching transports
        setSelectedBatchTransportId(null);
        setBatchNotes([]);
        setShowNotesView(false);
        setShowDriverDataView(false);
        setShowTimeWindowEdit(false);
        setShowAssortmentDistribution(false);
        setShowAddBatchTransport(false);
        setSupplierLIFNR(null); // Reset LIFNR

        try {
            const transport = state.items.find(t => t._id === transportId);
            if (!transport || !transport.TRANSPORTNUMMER) {
                setBatchTransports([]);
                return;
            }

            // Load LIFNR from contract (for loading locations ValueHelp)
            if (transport.VERTRAG) {
                setLoadingSupplierLIFNR(true);
                try {
                    const lifnr = await fetchLIFNRByContractNumber(String(transport.VERTRAG));
                    setSupplierLIFNR(lifnr);
                } catch (error) {
                    console.warn("Failed to fetch LIFNR, continuing without it:", error);
                    setSupplierLIFNR(null);
                }
                setLoadingSupplierLIFNR(false);
            }

            // Fetch batchTransports for this TRANSPORTNUMMER
            const baseUrl = API_BASE_URL || window.location.origin;
            const url = new URL(`/api/batch-transports`, baseUrl);
            url.searchParams.set("TransportNumber", transport.TRANSPORTNUMMER);
            url.searchParams.set("sort", "timeWindowFrom:asc");

            const response = await fetch(url.toString(), {
                headers: getAuthHeaders(),
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch batch transports (${response.status})`);
            }

            const items = (await response.json()) as Array<Record<string, unknown>>;
            setBatchTransports(Array.isArray(items) ? items : []);
        } catch (error) {
            console.error("Failed to load batch transports:", error);
            setBatchTransports([]);
        } finally {
            setLoadingBatchTransports(false);
        }
    }, [state.items]);

    const handleCloseDetail = useCallback(() => {
        setSelectedTransportId(null);
        setBatchTransports([]);
        setSelectedBatchTransportId(null);
        setShowNotesView(false);
        setBatchNotes([]);
        setShowDriverDataView(false);
    }, []);

    const handleBatchTransportClick = useCallback((batchTransportId: string) => {
        setSelectedBatchTransportId(batchTransportId);
        setShowNotesView(false); // Close notes view when selecting a different batch
        setShowDriverDataView(false); // Close driver data view when selecting a different batch
        setShowAssortmentDistribution(false); // Close assortment distribution when selecting a different batch
    }, []);

    const handleShowNotes = useCallback(() => {
        if (!selectedBatchTransportId) {
            return;
        }

        // Load notes from selected batch transport
        const selectedBatch = batchTransports.find(
            batch => String(batch._id) === selectedBatchTransportId
        );

        if (selectedBatch) {
            // Extract notes from qaNote1-qaNote5 fields
            const loadedNotes = [
                selectedBatch.qaNote1,
                selectedBatch.qaNote2,
                selectedBatch.qaNote3,
                selectedBatch.qaNote4,
                selectedBatch.qaNote5
            ]
                .filter((note): note is string => typeof note === "string" && note.trim().length > 0);

            setBatchNotes(loadedNotes);
        }

        setShowNotesView(true);
    }, [selectedBatchTransportId, batchTransports]);

    const handleCloseNotes = useCallback(() => {
        setShowNotesView(false);
    }, []);

    const handleShowDriverData = useCallback(() => {
        if (!selectedBatchTransportId) {
            return;
        }
        setShowDriverDataView(true);
        setShowNotesView(false); // Close notes view when opening driver data
    }, [selectedBatchTransportId]);

    const handleCloseDriverData = useCallback(() => {
        setShowDriverDataView(false);
    }, []);

    const handleShowTimeWindowEdit = useCallback(() => {
        if (!selectedBatchTransportId) {
            return;
        }
        setShowTimeWindowEdit(true);
        setShowNotesView(false);
        setShowDriverDataView(false);
    }, [selectedBatchTransportId]);

    const handleCloseTimeWindowEdit = useCallback(() => {
        setShowTimeWindowEdit(false);
    }, []);

    const handleShowAddBatchTransport = useCallback(() => {
        setShowAddBatchTransport(true);
        setShowNotesView(false);
        setShowDriverDataView(false);
        setShowTimeWindowEdit(false);
    }, []);

    const handleCloseAddBatchTransport = useCallback(() => {
        setShowAddBatchTransport(false);
    }, []);

    const handleShowAssortmentDistribution = useCallback(() => {
        if (!selectedBatchTransportId) {
            return;
        }
        setShowAssortmentDistribution(true);
        setShowNotesView(false);
        setShowDriverDataView(false);
        setShowTimeWindowEdit(false);
        setShowAddBatchTransport(false);
    }, [selectedBatchTransportId]);

    const handleCloseAssortmentDistribution = useCallback(() => {
        setShowAssortmentDistribution(false);
    }, []);

    const handleAssortmentDistributionSave = useCallback(() => {
        // Reload batch transports to reflect updated assortment distribution
        if (selectedTransportId) {
            void handleRowClick(selectedTransportId);
        }
    }, [selectedTransportId, handleRowClick]);

    const handleDriverDataChange = useCallback(async (data: {
        driverName?: string | null;
        truckLicensePlate?: string | null;
        loadingLocationName?: string | null;
        supplierReferenceNumber?: string | null;
    }) => {
        // Auto-save driver data to backend
        if (!selectedBatchTransportId) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/batch-transports/${selectedBatchTransportId}`, {
                method: "PATCH",
                credentials: "include",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`Failed to save driver data (${response.status})`);
            }

            // Update local batch transport data
            setBatchTransports(prev =>
                prev.map(batch =>
                    String(batch._id) === selectedBatchTransportId
                        ? { ...batch, ...data }
                        : batch
                )
            );
        } catch (error) {
            console.error("Failed to save driver data:", error);
            throw error; // Re-throw so DriverDataView can show error
        }
    }, [selectedBatchTransportId]);

    const handleTimeWindowChange = useCallback(async (
        newTimeWindowId: string,
        timeWindowData: {
            timeWindowFrom: string;
            timeWindowTo: string;
            deliverydate: string;
        }
    ) => {
        if (!selectedBatchTransportId) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/batch-transports/${selectedBatchTransportId}`, {
                method: "PATCH",
                credentials: "include",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    timeWindowId: newTimeWindowId,
                    timeWindowFrom: timeWindowData.timeWindowFrom,
                    timeWindowTo: timeWindowData.timeWindowTo,
                    deliverydate: timeWindowData.deliverydate
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to update time window (${response.status})`);
            }

            // Reload batch transports to show updated time window
            if (selectedTransportId) {
                await handleRowClick(selectedTransportId);
            }
        } catch (error) {
            console.error("Failed to update time window:", error);
            throw error;
        }
    }, [selectedBatchTransportId, selectedTransportId, handleRowClick]);

    const handleNotesChange = useCallback(async (notes: string[]) => {
        setBatchNotes(notes);

        // Auto-save notes to backend
        if (!selectedBatchTransportId) {
            return;
        }

        try {
            const notesData: Record<string, string | null> = {
                qaNote1: notes[0] || null,
                qaNote2: notes[1] || null,
                qaNote3: notes[2] || null,
                qaNote4: notes[3] || null,
                qaNote5: notes[4] || null
            };

            const response = await fetch(`${API_BASE_URL}/api/batch-transports/${selectedBatchTransportId}`, {
                method: "PATCH",
                credentials: "include",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(notesData)
            });

            if (!response.ok) {
                throw new Error(`Failed to save notes (${response.status})`);
            }

            // Update local batch transport data
            setBatchTransports(prev =>
                prev.map(batch =>
                    String(batch._id) === selectedBatchTransportId
                        ? { ...batch, ...notesData }
                        : batch
                )
            );
        } catch (error) {
            console.error("Failed to save notes:", error);
            alert("Fehler beim Speichern der Notizen");
        }
    }, [selectedBatchTransportId]);

    const handleRefreshBatchTransports = useCallback(async () => {
        if (!selectedTransportId) {
            return;
        }
        await handleRowClick(selectedTransportId);
    }, [selectedTransportId, handleRowClick]);

    const handleDeleteBatchTransport = useCallback(async (batchTransportId: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Verhindert dass die Zeile selektiert wird

        if (!window.confirm("Möchten Sie diese Charge wirklich löschen?")) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/batch-transports/${batchTransportId}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
                credentials: "include",
            });

            if (response.status === 401) {
                const message = await extractErrorMessage(response);
                throw new Error(message ?? "Authentifizierung erforderlich. Bitte melde dich erneut an.");
            }

            if (!response.ok) {
                const message = await extractErrorMessage(response);
                throw new Error(message ?? `Löschen fehlgeschlagen (${response.status})`);
            }

            // Entferne die Charge aus der lokalen Liste
            setBatchTransports(prev => prev.filter(batch => String(batch._id) !== batchTransportId));

            // Deselektiere wenn die gelöschte Charge selektiert war
            if (selectedBatchTransportId === batchTransportId) {
                setSelectedBatchTransportId(null);
            }

            // Aktualisiere die Transport-Liste um die Mengen zu aktualisieren
            await fetchTransports();
        } catch (error) {
            const message = error instanceof Error ? error.message : "Fehler beim Löschen der Charge";
            alert(message);
            console.error("Failed to delete batch transport:", error);
        }
    }, [selectedBatchTransportId, fetchTransports]);

    const handlePrintQRCode = useCallback(async () => {
        if (!selectedBatchTransportId) {
            return;
        }

        try {
            // Finde den aktuellen Batch Transport
            const batchTransport = batchTransports.find(b => String(b._id) === selectedBatchTransportId);
            if (!batchTransport?.transportBatchNumber) {
                alert("Keine Transportchargennummer gefunden");
                return;
            }

            const qrCodeData = String(batchTransport.transportBatchNumber);

            // Generiere QR Code als Data URL
            const qrCodeDataUrl = await QRCode.toDataURL(qrCodeData, {
                width: 150,
                margin: 1,
                errorCorrectionLevel: "M"
            });

            // Erstelle ein verstecktes Druckfenster
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
                alert("Popup-Blocker verhindert das Drucken. Bitte erlauben Sie Popups für diese Seite.");
                return;
            }

            // Schreibe HTML für Druckvorschau
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>QR Code - ${qrCodeData}</title>
                    <style>
                        @media print {
                            @page { margin: 0; }
                            body { margin: 1cm; }
                        }
                        body {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            min-height: 100vh;
                            margin: 0;
                            font-family: Arial, sans-serif;
                        }
                        .container {
                            text-align: center;
                        }
                        img {
                            max-width: 150px;
                            height: auto;
                        }
                        .label {
                            margin-top: 10px;
                            font-size: 18px;
                            font-weight: bold;
                        }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <img src="${qrCodeDataUrl}" alt="QR Code" />
                        <div class="label">${qrCodeData}</div>
                    </div>
                </body>
                </html>
            `);

            printWindow.document.close();

            // Warte bis Bild geladen ist, dann drucke
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 250);
            };
        } catch (error) {
            console.error("Failed to generate QR code:", error);
            alert("Fehler beim Generieren des QR Codes");
        }
    }, [selectedBatchTransportId, batchTransports]);

    const handleSort = useCallback(
        (column: ColumnDefinition) => {
            if (!column.sortable) {
                return;
            }

            if (sortConfig && sortConfig.key === column.key) {
                const nextDirection = sortConfig.direction === "asc" ? "desc" : "asc";
                setSortParam(buildSortParam(column, nextDirection));
            } else {
                setSortParam(buildSortParam(column, "asc"));
            }
        },
        [sortConfig]
    );

    const handleFilterChange = useCallback((field: FilterField, value: string | string[]) => {
        setFilterForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleFilterSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setAppliedFilters(buildAppliedFilters(filterForm));
        },
        [filterForm]
    );

    const handleFilterReset = useCallback(() => {
        const emptyForm: FilterFormState = {
            TRANSPORTNUMMER: "",
            FRAKTION: "",
            SupplierName: "",
            VERTART: "",
            WERKS: ["0128", "0123"],
            State: "",
            KALENDERWOCHE: "",
            Sortimentbez: "",
        };
        setFilterForm(emptyForm);
        // Reset zu den initialen Filtern (minKW + beide Werke)
        const minKW = getMinimumCalendarWeek();
        setAppliedFilters({
            minKALENDERWOCHE: String(minKW),
            WERKS: "0128,0123"
        });
    }, []);

    const renderSortIndicator = (column: ColumnDefinition) => {
        if (!column.sortable || !sortConfig || sortConfig.key !== column.key) {
            return null;
        }

        return (
            <span className="transport-list-sort-indicator">
                {sortConfig.direction === "asc" ? " ↑" : " ↓"}
            </span>
        );
    };

    const renderTruckIcon = (state: string, color: string, titleText: string) => {
        return (
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke={color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-label={titleText}
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
            >
                <title>{titleText}</title>
                <path d="M1 3h15v13H1z"></path>
                <path d="M16 8h3l3 3v5h-6V8z"></path>
                <circle cx="5.5" cy="18.5" r="2.5" fill={color}></circle>
                <circle cx="18.5" cy="18.5" r="2.5" fill={color}></circle>
            </svg>
        );
    };

    const renderStateIcon = (transport: Transport): ReactElement => {
        const totalQuantityStr = transport.MENGE ?? "0";
        const totalQuantity = parseInt(totalQuantityStr) || 0;
        const plannedStr = transport.plannedQuantity ?? "0";
        const planned = parseInt(plannedStr) || 0;

        let color: string;
        let label: string;
        let title: string;

        if (planned === 0) {
            // Rot: Noch nichts geplant
            color = "#dc2626";
            label = "Ungeplant";
            title = "Ungeplant - Noch keine Planung vorhanden";
        } else if (planned >= totalQuantity) {
            // Grün: Vollständig geplant
            color = "#16a34a";
            label = "Verplant";
            title = "Verplant - Vollständig eingeplant";
        } else {
            // Orange: Teilweise geplant
            color = "#f59e0b";
            label = "Teilweise verplant";
            title = "Teilweise verplant - Planung läuft";
        }

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {renderTruckIcon(label, color, title)}
            </div>
        );
    };


    // Fraktionsnamen-Mapping (FRAKTION → ZEITFENSTER_GT) im Komponenten-Body
    const [fractionNameMap, setFractionNameMap] = useState<Record<string, string>>({});
    useEffect(() => {
        fetchZhbT49GtMap().then(setFractionNameMap).catch(() => setFractionNameMap({}));
    }, []);

    // Helper analog zu carrierName
    const getFractionName = (row: Transport): string => {
        if (!row.FRAKTION) return "–";
        // 1. Direkter Treffer
        if (fractionNameMap[row.FRAKTION]) {
            return fractionNameMap[row.FRAKTION];
        }
        // 2. Versuche Match ohne führende Nullen
        const fraktionStr = String(row.FRAKTION).replace(/^0+/, "");
        for (const [key, value] of Object.entries(fractionNameMap)) {
            if (key.replace(/^0+/, "") === fraktionStr) {
                return value;
            }
        }
        return row.FRAKTION || "–";
    };

    const renderCellValue = (column: ColumnDefinition, row: Transport): string | ReactElement | null => {
        const rawValue = row[column.key];

        if (column.key === "State") {
            return renderStateIcon(row);
        }

        // Fraktionsname analog zu carrierName
        if (column.key === "FRAKTION") {
            return getFractionName(row);
        }


        // SupplierName mit Tooltip und einheitlicher Breite
        if (column.key === "SupplierName") {
            if (!rawValue) return "–";
            return (
                <span title={String(rawValue)} style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    maxWidth: 94,
                    verticalAlign: 'middle',
                    cursor: 'pointer'
                }}>
                    {String(rawValue)}
                </span>
            );
        }

        // carrierName mit Tooltip und einheitlicher Breite, Fallback zu SPEDITEUR
        if (column.key === "carrierName") {
            const value = rawValue || row["SPEDITEUR"] || "";
            if (!value) return "–";
            return (
                <span title={String(value)} style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    maxWidth: 94,
                    verticalAlign: 'middle',
                    cursor: 'pointer'
                }}>
                    {String(value)}
                </span>
            );
        }

        // Spezielle Behandlung für plannedQuantity, unplannedQuantity und batchTransportsCount: Default 0
        if (column.key === "plannedQuantity" || column.key === "unplannedQuantity" || column.key === "batchTransportsCount") {
            const value = rawValue ?? 0;
            return formatResultValue(value, "de", column.format || NUMBER_FORMAT);
        }

        // Vertragsdaten: Default "-"
        if (column.key === "VERTRAG" || column.key === "VERTRAGSPOSITION") {
            return rawValue ? String(rawValue) : "-";
        }

        if (column.format) {
            return formatResultValue(rawValue, "de", column.format);
        }

        if (rawValue === null || rawValue === undefined) {
            return "–";
        }

        return String(rawValue);
    };

    const isLoading = state.status === "loading";
    const hasError = state.status === "error";
    const isEmpty = state.status === "loaded" && state.items.length === 0;

    const selectedTransport = useMemo(() => {
        if (!selectedTransportId) {
            return null;
        }
        return state.items.find((t) => t._id === selectedTransportId) ?? null;
    }, [selectedTransportId, state.items]);

    const handleSaveBatchTransports = useCallback(
        async (allocations: Array<{ timeWindowId: string; quantityTrucks: number; timeWindowFrom?: string; timeWindowTo?: string; day?: string; timeSlot?: string }>) => {
            if (!selectedTransport) return;

            try {
                // Generiere Batch Transport Nummern
                const existingNumbers = batchTransports
                    .map(bt => String(bt.transportBatchNumber))
                    .filter(num => num.startsWith(selectedTransport.TRANSPORTNUMMER || ""));

                let nextNumber = 1;
                if (existingNumbers.length > 0) {
                    const maxNumber = Math.max(
                        ...existingNumbers.map(num => {
                            const suffix = num.slice(-3);
                            return parseInt(suffix) || 0;
                        })
                    );
                    nextNumber = maxNumber + 1;
                }

                // Erstelle Batch Transports
                for (const allocation of allocations) {
                    const batchNumber = `${selectedTransport.TRANSPORTNUMMER}${String(nextNumber).padStart(3, "0")}`;

                    // Wenn Zeitfenster-Daten direkt mitgeliefert wurden, verwende sie
                    let deliverydate: string;
                    let timeWindowFrom = allocation.timeWindowFrom;
                    let timeWindowTo = allocation.timeWindowTo;

                    if (timeWindowFrom && timeWindowTo) {
                        // Zeitfenster-Daten sind bereits vorhanden
                        deliverydate = timeWindowFrom.split("T")[0];
                    } else {
                        // Fallback: Verwende aktuelles Datum
                        deliverydate = new Date().toISOString().split("T")[0];
                    }

                    const fractionName = getFractionName(selectedTransport);

                    const payload = {
                        type: "batchTransport",

                        // Transport-Felder übernehmen (mit SAP-Feldnamen)
                        TransportNumber: selectedTransport.TRANSPORTNUMMER,
                        SupplierName: selectedTransport.SupplierName,
                        Sortimentbez: selectedTransport.Sortimentbez,
                        Fraction: selectedTransport.FRAKTION, // SAP code for reference
                        fraction: fractionName, // ZEITFENSTER_GT for capacity calculation
                        Contract: selectedTransport.VERTRAG,
                        ContractPos: selectedTransport.VERTRAGSPOSITION,
                        Vertart: selectedTransport.VERTART,
                        Plant: selectedTransport.WERKS,
                        plant: selectedTransport.WERKS, // Lowercase für Backend-Kapazitätsberechnung
                        CalWeek: selectedTransport.KALENDERWOCHE,
                        carrierName: selectedTransport.carrierName || selectedTransport.SPEDITEUR, // Spediteurname mit Fallback
                        SPEDITEUR: selectedTransport.SPEDITEUR, // SAP-Feld für Backend

                        // Batch-spezifische Felder
                        transportBatchNumber: batchNumber,
                        timeWindowId: allocation.timeWindowId,
                        quantityTrucks: allocation.quantityTrucks,
                        deliverydate,
                        timeWindowFrom,
                        timeWindowTo
                    };

                    const response = await fetch(`${API_BASE_URL}/api/batch-transports`, {
                        method: "POST",
                        credentials: "include",
                        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                        body: JSON.stringify(payload)
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to create batch transport (${response.status})`);
                    }

                    nextNumber++;
                }

                // Aktualisiere Listen
                await handleRefreshBatchTransports();
                await fetchTransports();
            } catch (error) {
                console.error("Failed to create batch transports:", error);
                throw error;
            }
        },
        [selectedTransport, batchTransports, handleRefreshBatchTransports, fetchTransports]
    );

    const handleNavigateDetail = useCallback(async (direction: "prev" | "next") => {
        if (!selectedTransportId || state.items.length === 0) {
            return;
        }

        const currentIndex = state.items.findIndex((t) => t._id === selectedTransportId);
        if (currentIndex === -1) {
            return;
        }

        let nextIndex: number;
        if (direction === "prev") {
            nextIndex = currentIndex > 0 ? currentIndex - 1 : state.items.length - 1;
        } else {
            nextIndex = currentIndex < state.items.length - 1 ? currentIndex + 1 : 0;
        }

        const nextTransportId = state.items[nextIndex]._id;

        // Load batch transports for the next transport
        await handleRowClick(nextTransportId);
    }, [selectedTransportId, state.items, handleRowClick]);

    const selectedTransportIndex = useMemo(() => {
        if (!selectedTransportId) {
            return null;
        }
        return state.items.findIndex((t) => t._id === selectedTransportId);
    }, [selectedTransportId, state.items]);

    const visibleItems = useMemo(() => {
        return state.items.slice(0, displayedItemsCount);
    }, [state.items, displayedItemsCount]);

    const handleLoadMore = useCallback(() => {
        setDisplayedItemsCount(prev => Math.min(prev + 20, state.items.length));
    }, [state.items.length]);

    const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;

        // Load more when scrolled to 80%
        if (scrollPercentage > 0.8 && displayedItemsCount < state.items.length) {
            handleLoadMore();
        }
    }, [displayedItemsCount, state.items.length, handleLoadMore]);

    return (
        <div className="transport-list-container">
            {selectedTransport ? (
                <main className="transport-list-shell">
                    <header className="transport-detail-header-main">
                        <div className="transport-detail-header-left">
                            <button
                                type="button"
                                className="transport-detail-back-button"
                                onClick={handleCloseDetail}
                                aria-label="Zurück zur Liste"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h1 className="transport-detail-title-main">Transport Details</h1>
                        </div>
                        <div className="transport-detail-navigation">
                            <button
                                type="button"
                                className="transport-detail-nav-button"
                                onClick={() => handleNavigateDetail("prev")}
                                aria-label="Vorheriger Transport"
                            >
                                ← Vorheriger
                            </button>
                            {selectedTransportIndex !== null && (
                                <span className="transport-detail-position">
                                    {selectedTransportIndex + 1} von {state.items.length}
                                </span>
                            )}
                            <button
                                type="button"
                                className="transport-detail-nav-button"
                                onClick={() => handleNavigateDetail("next")}
                                aria-label="Nächster Transport"
                            >
                                Nächster →
                            </button>
                        </div>
                    </header>
                    <div className="transport-detail-content">
                        <dl className="transport-detail-grid">
                            <div className="transport-detail-item">
                                <dt>
                                    <span className="transport-detail-icon">📦</span>{" "}
                                    {showNotesView || showDriverDataView || showTimeWindowEdit || showAssortmentDistribution ? "Chargen-Nummer" : "Transportnummer"}
                                </dt>
                                <dd>
                                    {showNotesView || showDriverDataView || showTimeWindowEdit || showAssortmentDistribution
                                        ? String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.transportBatchNumber ?? "–")
                                        : (selectedTransport.TRANSPORTNUMMER ?? "–")
                                    }
                                </dd>
                            </div>
                            <div className="transport-detail-item">
                                <dt><span className="transport-detail-icon">🏭</span> Lieferant</dt>
                                <dd>{selectedTransport.SupplierName ?? "–"}</dd>
                            </div>
                            {selectedTransport.MATNR && (
                                <div className="transport-detail-item">
                                    <dt><span className="transport-detail-icon">📋</span> Sortiment</dt>
                                    <dd>
                                        <SortimentNameDisplay
                                            matnr={selectedTransport.MATNR}
                                            fallback={selectedTransport.Sortimentbez}
                                        />
                                    </dd>
                                </div>
                            )}
                            <div className="transport-detail-item">
                                <dt><span className="transport-detail-icon">🌲</span> Fraktion</dt>
                                <dd>{getFractionName(selectedTransport)}</dd>
                            </div>
                            <div className="transport-detail-item">
                                <dt>
                                    <span className="transport-detail-icon">⏳</span>{" "}
                                    {showNotesView || showDriverDataView || showTimeWindowEdit || showAssortmentDistribution ? "Anzahl LKWs" : "Ungeplant"}
                                </dt>
                                <dd>
                                    {showNotesView || showDriverDataView || showTimeWindowEdit || showAssortmentDistribution
                                        ? String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.quantityTrucks ?? "–")
                                        : (selectedTransport.unplannedQuantity ?? 0)
                                    }
                                </dd>
                            </div>
                            <div className="transport-detail-item">
                                <dt><span className="transport-detail-icon">📅</span> Kalenderwoche</dt>
                                <dd>{selectedTransport.KALENDERWOCHE ?? "–"}</dd>
                            </div>

                            {/* Action Tiles */}
                            {!showNotesView && !showDriverDataView && !showTimeWindowEdit && !showAssortmentDistribution && !showAddBatchTransport && (
                                <>
                                    {/* Always visible: Chargen einplanen */}
                                    <button
                                        type="button"
                                        className="transport-detail-item transport-detail-action-tile-grid"
                                        onClick={handleShowAddBatchTransport}
                                        aria-label="Charge einplanen"
                                        title="Charge einplanen"
                                    >
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                        <span>Chargen einplanen</span>
                                    </button>

                                    {/* Conditional tiles - only visible when batch transport is selected */}
                                    {selectedBatchTransportId && (
                                        <>
                                            {getFractionName(selectedTransport) === "WALDHOLZ" && !selectedTransport?.VERTRAG && (
                                                <button
                                                    type="button"
                                                    className="transport-detail-item transport-detail-action-tile-grid"
                                                    onClick={handleShowAssortmentDistribution}
                                                    aria-label="Sortiment"
                                                    title="Sortiment"
                                                >
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <rect x="3" y="3" width="7" height="7"></rect>
                                                        <rect x="14" y="3" width="7" height="7"></rect>
                                                        <rect x="14" y="14" width="7" height="7"></rect>
                                                        <rect x="3" y="14" width="7" height="7"></rect>
                                                    </svg>
                                                    <span>Sortiment</span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="transport-detail-item transport-detail-action-tile-grid"
                                                onClick={handleShowNotes}
                                                aria-label="Notizen"
                                                title="Notizen"
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span>Notizen</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="transport-detail-item transport-detail-action-tile-grid"
                                                onClick={handleShowDriverData}
                                                aria-label="Fahrerdaten"
                                                title="Fahrerdaten"
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                                <span>Fahrerdaten</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="transport-detail-item transport-detail-action-tile-grid"
                                                onClick={handleShowTimeWindowEdit}
                                                aria-label="Zeitfenster ändern"
                                                title="Zeitfenster ändern"
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                                <span>Zeitfenster</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="transport-detail-item transport-detail-action-tile-grid"
                                                onClick={handlePrintQRCode}
                                                aria-label="QR-Code drucken"
                                                title="QR-Code drucken"
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="3" y="3" width="7" height="7"></rect>
                                                    <rect x="14" y="3" width="7" height="7"></rect>
                                                    <rect x="3" y="14" width="7" height="7"></rect>
                                                    <polyline points="14 17 14 21 21 21 21 14"></polyline>
                                                </svg>
                                                <span>QR-Code</span>
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </dl>

                        {showNotesView ? (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Notizen zur Charge</h2>
                                    <button
                                        type="button"
                                        className="transport-detail-action-button"
                                        onClick={handleCloseNotes}
                                        aria-label="Zurück"
                                        title="Zurück zur Chargen-Übersicht"
                                    >
                                        ← Zurück
                                    </button>
                                </div>
                                <NotesView
                                    notes={batchNotes}
                                    onNotesChange={handleNotesChange}
                                    maxNotes={5}
                                    maxLength={70}
                                    title="Chargen-Bemerkungen"
                                    placeholder="Neue Bemerkung eingeben"
                                />
                            </>
                        ) : showDriverDataView ? (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Fahrerdaten</h2>
                                    <button
                                        type="button"
                                        className="transport-detail-action-button"
                                        onClick={handleCloseDriverData}
                                        aria-label="Zurück"
                                        title="Zurück zur Chargen-Übersicht"
                                    >
                                        ← Zurück
                                    </button>
                                </div>
                                {selectedBatchTransportId && (
                                    <DriverDataView
                                        batchTransportId={selectedBatchTransportId}
                                        lifnr={supplierLIFNR || undefined}
                                        initialData={{
                                            driverName: String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.driverName ?? ""),
                                            truckLicensePlate: String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.truckLicensePlate ?? ""),
                                            loadingLocationName: String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.loadingLocationName ?? ""),
                                            supplierReferenceNumber: String(batchTransports.find(b => String(b._id) === selectedBatchTransportId)?.supplierReferenceNumber ?? "")
                                        }}
                                        onClose={handleCloseDriverData}
                                        onChange={handleDriverDataChange}
                                    />
                                )}
                            </>
                        ) : showTimeWindowEdit ? (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Zeitfenster ändern</h2>
                                    <div className="transport-detail-action-buttons">
                                        <button
                                            type="button"
                                            className="transport-detail-action-button transport-detail-save-button"
                                            onClick={async () => {
                                                if (timeWindowEditSaveHandler) {
                                                    await timeWindowEditSaveHandler();
                                                }
                                            }}
                                            disabled={!canSaveTimeWindowEdit}
                                            aria-label="Zeitfenster ändern"
                                            title="Zeitfenster ändern"
                                        >
                                            Zeitfenster ändern
                                        </button>
                                        <button
                                            type="button"
                                            className="transport-detail-action-button"
                                            onClick={handleCloseTimeWindowEdit}
                                            aria-label="Zurück"
                                            title="Zurück zur Chargen-Übersicht"
                                        >
                                            ← Zurück
                                        </button>
                                    </div>
                                </div>
                                {selectedBatchTransportId && (() => {
                                    const selectedBatch = batchTransports.find(b => String(b._id) === selectedBatchTransportId);

                                    return selectedBatch ? (
                                        <TimeWindowEditView
                                            batchTransportId={selectedBatchTransportId}
                                            currentTimeWindowId={String(selectedBatch.timeWindowId ?? "")}
                                            calWeek={selectedTransport.KALENDERWOCHE || ""}
                                            plant={selectedTransport.WERKS || ""}
                                            fractionBez={getFractionName(selectedTransport)}
                                            onSave={handleTimeWindowChange}
                                            onClose={handleCloseTimeWindowEdit}
                                            onSaveReady={(saveHandler, canSave) => {
                                                setTimeWindowEditSaveHandler(() => saveHandler);
                                                setCanSaveTimeWindowEdit(canSave);
                                            }}
                                        />
                                    ) : null;
                                })()}
                            </>
                        ) : showAssortmentDistribution ? (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Sortimentsverteilung</h2>
                                    <button
                                        type="button"
                                        className="transport-detail-action-button"
                                        onClick={handleCloseAssortmentDistribution}
                                        aria-label="Zurück"
                                        title="Zurück zur Chargen-Übersicht"
                                    >
                                        ← Zurück
                                    </button>
                                </div>
                                {selectedBatchTransportId && (
                                    <AssortmentDistributionView
                                        batchTransportId={selectedBatchTransportId}
                                        onClose={handleCloseAssortmentDistribution}
                                        onSave={handleAssortmentDistributionSave}
                                    />
                                )}
                            </>
                        ) : showAddBatchTransport ? (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Charge einplanen</h2>
                                    <div className="transport-detail-action-buttons">
                                        {batchTransportOverage > 0 && (
                                            <span className="transport-detail-overage-warning">
                                                Sie haben {batchTransportOverage} LKW{batchTransportOverage === 1 ? '' : 's'} zu viel eingeplant
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className={`transport-detail-action-button transport-detail-save-button ${batchTransportOverage > 0 ? 'transport-detail-save-button--error' : ''}`}
                                            onClick={async () => {
                                                if (batchTransportSaveHandler) {
                                                    await batchTransportSaveHandler();
                                                }
                                            }}
                                            disabled={!canSaveBatchTransport}
                                            aria-label="Transport einplanen"
                                            title="Transport einplanen"
                                        >
                                            Transport einplanen
                                        </button>
                                        <button
                                            type="button"
                                            className="transport-detail-action-button"
                                            onClick={handleCloseAddBatchTransport}
                                            aria-label="Zurück"
                                            title="Zurück zur Chargen-Übersicht"
                                        >
                                            ← Zurück
                                        </button>
                                    </div>
                                </div>
                                <AddBatchTransportView
                                    transportNumber={selectedTransport.TRANSPORTNUMMER || ""}
                                    fractionBez={getFractionName(selectedTransport)}
                                    calWeek={selectedTransport.KALENDERWOCHE || ""}
                                    plant={selectedTransport.WERKS || ""}
                                    maxQuantity={selectedTransport.unplannedQuantity ? parseInt(selectedTransport.unplannedQuantity) : undefined}
                                    existingBatchNumbers={batchTransports.map(bt => String(bt.transportBatchNumber))}
                                    onClose={handleCloseAddBatchTransport}
                                    onSave={handleSaveBatchTransports}
                                    onSaveReady={(saveHandler, canSave) => {
                                        setBatchTransportSaveHandler(() => saveHandler);
                                        setCanSaveBatchTransport(canSave);
                                    }}
                                    onOverageChange={(overage) => {
                                        setBatchTransportOverage(overage);
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <div className="transport-detail-section-header">
                                    <h2 className="transport-detail-section-title">Geplante Chargen</h2>
                                </div>

                                {loadingBatchTransports ? (
                                    <div className="transport-detail-loading">
                                        <span className="transport-detail-spinner" aria-hidden="true" />
                                        <p>Lade Chargen...</p>
                                    </div>
                                ) : batchTransports.length === 0 ? (
                                    <div className="transport-detail-empty">
                                        <p>Noch keine Chargen geplant</p>
                                    </div>
                                ) : (
                                    <div className="transport-detail-batch-list">
                                        <table className="transport-detail-batch-table">
                                            <thead>
                                                <tr>
                                                    <th>Chargen-Nummer</th>
                                                    <th>Zeitfenster Von</th>
                                                    <th>Zeitfenster Bis</th>
                                                    <th>LKWs</th>
                                                    <th>Kennzeichen</th>
                                                    <th>Fahrer</th>
                                                    <th>Spediteur</th>
                                                    <th className="transport-detail-batch-table-action-header">
                                                        <button
                                                            type="button"
                                                            className="transport-detail-refresh-button"
                                                            onClick={handleRefreshBatchTransports}
                                                            disabled={loadingBatchTransports}
                                                            aria-label="Chargen aktualisieren"
                                                            title="Chargen aktualisieren"
                                                        >
                                                            <svg
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <polyline points="23 4 23 10 17 10"></polyline>
                                                                <polyline points="1 20 1 14 7 14"></polyline>
                                                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                                                            </svg>
                                                        </button>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {batchTransports.map((batch) => (
                                                    <tr
                                                        key={String(batch._id)}
                                                        onClick={() => handleBatchTransportClick(String(batch._id))}
                                                        className={selectedBatchTransportId === String(batch._id) ? 'transport-detail-batch-table-row-selected' : ''}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>{String(batch.transportBatchNumber ?? "–")}</td>
                                                        <td>{batch.timeWindowFrom ? formatResultValue(batch.timeWindowFrom, "de", { kind: "datetime" }) : "–"}</td>
                                                        <td>{batch.timeWindowTo ? formatResultValue(batch.timeWindowTo, "de", { kind: "datetime" }) : "–"}</td>
                                                        <td>{String(batch.quantityTrucks ?? "–")}</td>
                                                        <td>{String(batch.truckLicensePlate ?? "–")}</td>
                                                        <td>{String(batch.driverName ?? "–")}</td>
                                                        <td>{String(batch.carrierName ?? "–")}</td>
                                                        <td className="transport-detail-batch-table-action-cell">
                                                            <button
                                                                type="button"
                                                                className="transport-detail-delete-button"
                                                                onClick={(e) => handleDeleteBatchTransport(String(batch._id), e)}
                                                                aria-label="Charge löschen"
                                                                title="Charge löschen"
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                >
                                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </main>
            ) : (
                <div className="transport-list-shell">
                    <div className="transport-list-content">
                        <section className={`transport-list-filters ${isFilterCollapsed ? 'transport-list-filters--collapsed' : ''}`} aria-label={PAGE_TEXT.filters.title}>
                            <form className="transport-list-filter-form" onSubmit={handleFilterSubmit}>
                                <header className="transport-list-filter-header">
                                    <h2>{PAGE_TEXT.filters.title}</h2>
                                    <div className="transport-list-filter-header-buttons">
                                        <button
                                            type="button"
                                            className="transport-list-refresh-icon-button"
                                            onClick={handleRefresh}
                                            disabled={isLoading}
                                            aria-label="Aktualisieren"
                                            title="Aktualisieren"
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="23 4 23 10 17 10"></polyline>
                                                <polyline points="1 20 1 14 7 14"></polyline>
                                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="transport-list-filter-toggle"
                                            onClick={toggleFilterCollapse}
                                            aria-expanded={!isFilterCollapsed}
                                            aria-label={isFilterCollapsed ? "Filter einblenden" : "Filter ausblenden"}
                                        >
                                            <span className="transport-list-filter-toggle-icon" aria-hidden="true">
                                                {isFilterCollapsed ? "▼" : "▲"}
                                            </span>
                                        </button>
                                    </div>
                                </header>
                                <div className="transport-list-filter-grid">
                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-state">
                                            {PAGE_TEXT.filters.State}
                                        </label>
                                        <select
                                            id="filter-state"
                                            className="transport-list-filter-input"
                                            value={filterForm.State}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                handleFilterChange("State", e.target.value)
                                            }
                                        >
                                            <option value="">Alle</option>
                                            <option value="ungeplant">Ungeplant</option>
                                            <option value="teilweise verplant">Teilweise verplant</option>
                                            <option value="verplant">Verplant</option>
                                        </select>
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-transportnumber">
                                            {PAGE_TEXT.filters.TRANSPORTNUMMER}
                                        </label>
                                        <input
                                            id="filter-transportnumber"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.TRANSPORTNUMMER}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("TRANSPORTNUMMER", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.TRANSPORTNUMMER}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-fraktion">
                                            {PAGE_TEXT.filters.FRAKTION}
                                        </label>
                                        <input
                                            id="filter-fraktion"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.FRAKTION}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("FRAKTION", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.FRAKTION}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-sortimentbez">
                                            {PAGE_TEXT.filters.Sortimentbez}
                                        </label>
                                        <input
                                            id="filter-sortimentbez"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.Sortimentbez}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("Sortimentbez", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.Sortimentbez}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-suppliername">
                                            {PAGE_TEXT.filters.SupplierName}
                                        </label>
                                        <input
                                            id="filter-suppliername"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.SupplierName}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("SupplierName", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.SupplierName}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-vertart">
                                            {PAGE_TEXT.filters.VERTART}
                                        </label>
                                        <input
                                            id="filter-vertart"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.VERTART}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("VERTART", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.VERTART}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-calweek">
                                            {PAGE_TEXT.filters.KALENDERWOCHE}
                                        </label>
                                        <input
                                            id="filter-calweek"
                                            type="text"
                                            className="transport-list-filter-input"
                                            value={filterForm.KALENDERWOCHE}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFilterChange("KALENDERWOCHE", e.target.value)
                                            }
                                            placeholder={PAGE_TEXT.filters.KALENDERWOCHE}
                                        />
                                    </div>

                                    <div className="transport-list-filter-field">
                                        <label className="transport-list-filter-label" htmlFor="filter-plant">
                                            {PAGE_TEXT.filters.WERKS}
                                        </label>
                                        <MultiSelectComboBox
                                            id="filter-plant"
                                            options={[
                                                { value: "0128", label: "0128" },
                                                { value: "0123", label: "0123" }
                                            ]}
                                            selected={filterForm.WERKS}
                                            onChange={(selected) => handleFilterChange("WERKS", selected)}
                                            placeholder="Werk auswählen..."
                                        />
                                    </div>
                                </div>

                                <div className="transport-list-filter-actions">
                                    <button type="submit" className="transport-list-filter-apply">
                                        {PAGE_TEXT.filters.apply}
                                    </button>
                                    <button
                                        type="button"
                                        className="transport-list-filter-reset"
                                        onClick={handleFilterReset}
                                    >
                                        {PAGE_TEXT.filters.reset}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {hasError && (
                            <div className="transport-list-error">
                                <p>{state.error}</p>
                            </div>
                        )}

                        {isLoading && (
                            <div className="transport-list-loading" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '3rem',
                                gap: '1rem'
                            }}>
                                <div style={{
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid #0070f3',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                <p style={{ color: '#666', fontSize: '14px' }}>{PAGE_TEXT.loading}</p>
                                <style>{`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `}</style>
                            </div>
                        )}

                        {isEmpty && !hasError && !isLoading && (
                            <div className="transport-list-empty">
                                <h3 className="transport-list-empty-title">{PAGE_TEXT.emptyTitle}</h3>
                                <p className="transport-list-empty-description">{PAGE_TEXT.emptyDescription}</p>
                            </div>
                        )}

                        {!isEmpty && !hasError && !isLoading && (
                            <div className="transport-list-table-wrapper" onScroll={handleTableScroll}>
                                <table className="transport-list-table">
                                    <thead className="transport-list-table-header">
                                        <tr>
                                            {COLUMNS.map((column) => (
                                                <th
                                                    key={String(column.key)}
                                                    className={`transport-list-table-header-cell ${column.sortable ? "transport-list-table-header-cell-sortable" : ""
                                                        }`}
                                                    style={{ width: column.width ? `${column.width}px` : undefined }}
                                                    onClick={() => handleSort(column)}
                                                >
                                                    {column.label}
                                                    {renderSortIndicator(column)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="transport-list-table-body">
                                        {visibleItems.map((transport) => (
                                            <tr
                                                key={transport._id}
                                                className="transport-list-table-row"
                                                onClick={() => handleRowClick(transport._id)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                {COLUMNS.map((column) => (
                                                    <td key={String(column.key)} className="transport-list-table-cell">
                                                        {renderCellValue(column, transport) ?? "—"}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
